import type { PricingReviewWrite } from './usePricingReviewWrite';
import type { ReceiptPriceLocator } from './usePoReceiptWrite';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  PricingHistoryMember,
  PricingHistoryOperation,
  PricingRounding,
  PricingCurrentRow,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useSession } from '../session';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import {
  isForbidden,
  shownState,
  unresolved,
  UnresolvedState,
  ForbiddenState,
  StaleNotice,
  refreshFailed,
} from '../ui/QueryStates';
import { IconInventory } from '../icons';
import { money } from '../money';

export const roundingLabels: Record<PricingRounding, string> = {
  cent: 'Up to $0.01',
  nickel: 'Up to $0.05',
  quarter: 'Up to $0.25',
  dollar: 'Up to $1.00',
  ending95: 'Up to an ending of .95',
};
export const goalPercent = (bps: number | null | undefined) =>
  bps == null
    ? 'No goal'
    : `${Math.trunc(bps / 100)}${
        bps % 100
          ? '.' +
            String(bps % 100)
              .padStart(2, '0')
              .replace(/0$/, '')
          : ''
      }%`;
export function goalDescription(
  r: Pick<PricingCurrentRow, 'effectiveGoalBps' | 'effectiveGoalSource'>,
) {
  if (r.effectiveGoalSource === 'never') return 'Never';
  if (r.effectiveGoalBps == null) return 'No goal';
  return `${goalPercent(r.effectiveGoalBps)} · ${r.effectiveGoalSource === 'own' ? 'Own' : 'Category'}`;
}
export function goalPriceDescription(
  r: Pick<PricingCurrentRow, 'goalPriceStatus' | 'goalPriceCents'>,
) {
  if (r.goalPriceStatus === 'ready' && r.goalPriceCents !== null)
    return money(r.goalPriceCents);
  return (
    {
      no_cost: 'No cost yet',
      no_goal: 'No goal',
      never: 'Never',
      overflow: 'Outside supported sell price',
      ready: 'Unavailable',
    }[r.goalPriceStatus] ?? 'No goal'
  );
}
type Kind = PricingHistoryOperation['kind'];
const labels: Record<Kind, string> = {
  auto_raise: 'Automatic raise',
  goal_raise: 'Raised to goal',
  bulk: 'Bulk price change',
  manual: 'Manual price edit',
  undo: 'Price change undone',
  goal_change: 'Goal or rounding change',
  tolerance_change: 'Supplier tolerance change',
};
export function PriceChangeMember({
  member: m,
  review,
  enabled = false,
  onInspect,
  undoSourceKind,
}: {
  member: PricingHistoryMember;
  undoSourceKind?: 'bulk' | 'goal';
  review?: PricingReviewWrite;
  enabled?: boolean;
  onInspect?: (id: string) => void;
}) {
  return (
    <li
      className="space-y-1 break-words rounded border border-border p-3 text-sm"
      data-price-member={m.id}
    >
      <p className="font-medium">
        {m.itemName ?? ('categoryKey' in m ? m.categoryKey : null) ?? 'Shop policy'} ·
        Change {m.ordinal + 1}
      </p>
      {m.kind === 'manual' ||
      m.kind === 'auto_raise' ||
      m.kind === 'goal_raise' ||
      m.kind === 'bulk' ||
      m.kind === 'undo' ? (
        <>
          <p>
            {money(m.beforeSellCents)} → {money(m.afterSellCents)}
          </p>
          <p className="text-muted-foreground">
            Last cost: {m.lastCostCents === null ? 'Unknown' : money(m.lastCostCents)}
          </p>
        </>
      ) : null}
      {m.kind === 'bulk' ? (
        <div className="space-y-2">
          <p>
            Bulk price change ·{' '}
            {goalDescription({
              effectiveGoalBps: m.goalBps,
              effectiveGoalSource: m.goalSource,
            })}{' '}
            · {roundingLabels[m.rounding]}
          </p>
          {m.undo ? (
            <p>
              Undone by {m.undo.actorName ?? 'shop member'} ·{' '}
              {new Date(m.undo.at).toLocaleString()}
              {onInspect ? (
                <Button
                  className="min-h-11"
                  disabled={review?.locked}
                  onClick={() => onInspect(m.undo!.operationId)}
                >
                  View Undo
                </Button>
              ) : null}
            </p>
          ) : null}
        </div>
      ) : null}
      {m.kind === 'goal_raise' ? (
        <div className="space-y-2">
          <p>
            Raised to goal · {goalPercent(m.goalBps)}{' '}
            {m.goalSource === 'own' ? 'item' : 'category'} goal ·{' '}
            {roundingLabels[m.rounding]}
          </p>
          {m.undo ? (
            <p>
              Undone by {m.undo.actorName ?? 'shop member'} ·{' '}
              {new Date(m.undo.at).toLocaleString()}
              {onInspect ? (
                <Button
                  className="min-h-11"
                  data-price-review-action=""
                  disabled={review?.locked}
                  onClick={() => onInspect(m.undo!.operationId)}
                >
                  View Undo
                </Button>
              ) : null}
            </p>
          ) : review ? (
            <Button
              className="min-h-11"
              data-price-review-action=""
              disabled={!enabled || review.hasDraft || !review.online}
              onClick={(e) => review.openGoalUndo(m.id, e.currentTarget)}
            >
              Undo goal raise…
            </Button>
          ) : null}
        </div>
      ) : null}
      {m.kind === 'auto_raise' ? (
        <>
          <p>
            Cost{' '}
            {m.previousLastCostCents === null
              ? 'previously unknown'
              : money(m.previousLastCostCents)}{' '}
            → {money(m.lastCostCents!)} · {goalPercent(m.goalBps)}{' '}
            {m.goalSource === 'own' ? 'item' : 'category'} goal ·{' '}
            {roundingLabels[m.rounding]}
          </p>
          <p>
            PO #{m.source.poNumber}
            {m.source.counter ? ' · Counter run' : ''} · {m.source.vendorName} ·{' '}
            {m.source.locationName}
          </p>
          <p>
            Received {new Date(m.source.receivedAt).toLocaleString()}
            {m.source.receivedCostProvenance === 'inherited_line'
              ? ' · Cost inherited from the order line'
              : ''}
          </p>
        </>
      ) : null}
      {m.kind === 'auto_raise' ? (
        <div className="space-y-2">
          {!m.review ? (
            <p>Review status unavailable.</p>
          ) : m.review.status === 'unreviewed' ? (
            <p>Unreviewed</p>
          ) : (
            <>
              <p>
                Reviewed by {m.review.reviewerName ?? 'shop member'} ·{' '}
                {new Date(m.review.reviewedAt).toLocaleString()}
              </p>
              {m.review.status === 'undone' ? (
                <p>
                  Undone by {m.review.undo.actorName ?? 'shop member'} ·{' '}
                  {new Date(m.review.undo.at).toLocaleString()}
                  {onInspect ? (
                    <Button
                      data-price-review-action=""
                      className="min-h-11"
                      disabled={review?.locked}
                      onClick={() =>
                        onInspect(
                          m.review.status === 'undone' ? m.review.undo.operationId : '',
                        )
                      }
                    >
                      View Undo
                    </Button>
                  ) : null}
                </p>
              ) : null}
            </>
          )}
          {review && m.review && m.review.status !== 'undone' ? (
            <div className="flex flex-wrap gap-2">
              {m.review.status === 'unreviewed' ? (
                <Button
                  className="min-h-11"
                  data-price-review-action=""
                  disabled={!enabled || review.hasDraft || !review.online}
                  onClick={(e) => review.beginKeep(m.id, e.currentTarget)}
                >
                  Keep
                </Button>
              ) : null}
              <Button
                className="min-h-11"
                data-price-review-action=""
                disabled={!enabled || review.hasDraft || !review.online}
                onClick={(e) => review.openUndo(m.id, e.currentTarget)}
              >
                Undo…
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {m.kind === 'undo' ? (
        <>
          <p>
            {undoSourceKind === 'bulk'
              ? 'Undo of a recorded bulk price change.'
              : 'Undo of a recorded price raise.'}
            {m.note ? ` Note: ${m.note}` : ''}
          </p>
          {onInspect ? (
            <Button
              disabled={review?.locked}
              onClick={() => onInspect(m.raiseOperationId)}
            >
              {undoSourceKind ? 'View original price operation' : 'View original raise'}
            </Button>
          ) : null}
        </>
      ) : null}
      {m.kind === 'goal_change' ? (
        <p>
          {m.beforeMode === null && m.afterMode === null
            ? `${m.beforeRounding ? roundingLabels[m.beforeRounding] : 'Default rounding'} → ${m.afterRounding ? roundingLabels[m.afterRounding] : 'Default rounding'}`
            : `${m.beforeGoalSource === null ? 'Unknown effective goal' : goalDescription({ effectiveGoalBps: m.beforeEffectiveGoalBps, effectiveGoalSource: m.beforeGoalSource })} → ${m.afterGoalSource === null ? 'Unknown effective goal' : goalDescription({ effectiveGoalBps: m.afterEffectiveGoalBps, effectiveGoalSource: m.afterGoalSource })}`}
        </p>
      ) : null}
      {m.kind === 'tolerance_change' ? (
        <p>
          Supplier tolerance:{' '}
          {m.beforeToleranceBps === null
            ? 'Default (0%)'
            : `${m.beforeToleranceBps / 100}%`}{' '}
          → {m.afterToleranceBps / 100}%
        </p>
      ) : null}
    </li>
  );
}
function Pager({
  offset,
  total,
  onPage,
  disabled = false,
}: {
  offset: number;
  total: number;
  onPage: (n: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Button
        className="min-h-11"
        disabled={disabled || offset === 0}
        onClick={() => onPage(Math.max(0, offset - 25))}
      >
        Previous changes
      </Button>
      <span className="text-sm tabular-nums">
        {total
          ? `${offset + 1}–${Math.min(total, offset + 25)} of ${total}`
          : '0 changes'}
      </span>
      <Button
        className="min-h-11"
        disabled={disabled || offset + 25 >= total}
        onClick={() => onPage(offset + 25)}
      >
        Next changes
      </Button>
    </div>
  );
}
function OperationUndoAction({
  operation,
  review,
  enabled,
}: {
  operation: PricingHistoryOperation;
  review?: PricingReviewWrite;
  enabled: boolean;
}) {
  if (!review || (operation.kind !== 'bulk' && operation.kind !== 'goal_raise'))
    return null;
  return (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground">
        Undo reviews the whole original operation, including prices outside this item
        filter.
      </p>
      <Button
        className="min-h-11"
        data-pricing-operation-undo-opener={operation.id}
        disabled={
          !enabled || review.hasDraft || review.batchLaunchersBlocked || !review.online
        }
        onClick={(e) => {
          if (!review.batchLaunchersBlocked)
            review.openOperationUndo(operation.id, e.currentTarget);
        }}
      >
        Preview Undo operation…
      </Button>
    </div>
  );
}
function MemberDetails({
  operation,
  itemId,
  review,
  blocked,
  receiptSummary,
  onInspect,
  onClose,
}: {
  operation: PricingHistoryOperation;
  receiptSummary?: boolean;
  itemId?: string;
  review?: PricingReviewWrite;
  blocked?: boolean;
  onInspect?: (id: string) => void;
  onClose: () => void;
}) {
  const trpc = useTRPC(),
    [offset, setOffset] = useState(0);
  const q = useQuery({
    ...trpc.pricing.historyMembers.queryOptions({
      operationId: operation.id,
      itemId,
      offset,
    }),
    retry: false,
    placeholderData: (old) => old,
  });
  if (isForbidden(q.error))
    return <ForbiddenState icon={IconInventory} what="price history" />;
  return (
    <Sheet open onClose={onClose} title={labels[operation.kind]}>
      <div
        data-pricing-history-surface=""
        data-pricing-history-focus=""
        tabIndex={-1}
        className="space-y-4"
      >
        {!receiptSummary ? (
          <OperationUndoAction
            operation={operation}
            review={review}
            enabled={!blocked && !q.isFetching && !q.isPlaceholderData && !q.isError}
          />
        ) : null}
        <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
        {q.isPlaceholderData ? (
          <p role="status">Loading this page; showing previous changes.</p>
        ) : null}
        {q.data ? (
          <>
            <ul className="space-y-2">
              {q.data.rows.map((m) => (
                <PriceChangeMember
                  key={m.id}
                  member={m}
                  undoSourceKind={operation.undoSourceScopeKind}
                  review={review}
                  enabled={
                    !blocked && !q.isFetching && !q.isPlaceholderData && !q.isError
                  }
                  onInspect={onInspect}
                />
              ))}
            </ul>
            <Pager
              offset={offset}
              total={q.data.total}
              disabled={review?.locked}
              onPage={(n) => {
                if (!review?.locked) setOffset(n);
              }}
            />
          </>
        ) : (
          <UnresolvedState
            state={unresolved(shownState(q, q.data))}
            icon={IconInventory}
            what="price changes"
            error={q.error}
            onRetry={() => void q.refetch()}
          />
        )}
      </div>
    </Sheet>
  );
}
export function PricingHistory({
  itemId,
  operationId,
  initialKind,
  receiptSummary = false,
  review,
  blocked = false,
}: {
  itemId?: string;
  operationId?: string;
  initialKind?: Kind;
  receiptSummary?: boolean;
  review?: PricingReviewWrite;
  blocked?: boolean;
}) {
  const session = useSession(),
    allowed = session.role === 'owner' || session.role === 'office',
    trpc = useTRPC(),
    [linkedId, setLinkedId] = useState<string>(),
    [offset, setOffset] = useState(0),
    [kind, setKind] = useState<Kind | undefined>(initialKind),
    [detail, setDetail] = useState<PricingHistoryOperation | null>(null);
  const q = useQuery({
    ...trpc.pricing.history.queryOptions({
      itemId,
      operationId: linkedId ?? operationId,
      kind: linkedId ? undefined : kind,
      offset: linkedId ? 0 : offset,
    }),
    enabled: allowed,
    retry: false,
    placeholderData: (old) => old,
  });
  // A receipt's summary is always its original operation, including in member details.
  const inspect = receiptSummary
    ? undefined
    : (id: string) => {
        if (!review?.locked) {
          setDetail(null);
          setLinkedId(id);
        }
      };
  if (!allowed || isForbidden(q.error))
    return <ForbiddenState icon={IconInventory} what="price history" />;
  return (
    <section
      data-pricing-history-surface=""
      className="space-y-3 border-t border-border pt-4"
      aria-label="Price history"
    >
      <h3 data-pricing-history-focus="" tabIndex={-1} className="font-semibold">
        Price history
      </h3>
      {receiptSummary && q.data?.rows[0] ? (
        <>
          <p role="status" className="text-sm">
            {q.data.rows[0].memberCount}{' '}
            {q.data.rows[0].memberCount === 1 ? 'price raise' : 'price raises'} across{' '}
            {q.data.rows[0].partCount} {q.data.rows[0].partCount === 1 ? 'part' : 'parts'}{' '}
            {q.data.rows[0].memberCount === 1 ? 'was' : 'were'} raised by this receipt.
          </p>
          <p className="text-sm text-muted-foreground">
            These prices were already live when this receipt completed. Later changes,
            including Undo, are recorded separately.
          </p>
        </>
      ) : null}
      {linkedId ? (
        <Button disabled={review?.locked} onClick={() => setLinkedId(undefined)}>
          Back to changes
        </Button>
      ) : null}
      {!operationId && !linkedId ? (
        <label className="block text-sm">
          Change type
          <select
            className="ml-2 min-h-11 rounded border border-border bg-surface px-2"
            value={kind ?? ''}
            disabled={review?.locked}
            onChange={(e) => {
              if (review?.locked) return;
              setKind((e.target.value || undefined) as Kind | undefined);
              setOffset(0);
            }}
          >
            <option value="">All changes</option>
            {Object.entries(labels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <StaleNotice show={refreshFailed(q, q.data)} onRetry={() => void q.refetch()} />
      {q.isPlaceholderData ? (
        <p role="status">Loading this page; showing previous changes.</p>
      ) : null}
      {q.data ? (
        <>
          {q.data.total === 0 ? (
            <p className="text-sm text-muted-foreground">
              No recorded price changes. Earlier catalogue prices were not backfilled.
            </p>
          ) : (
            q.data.rows.map((op) => (
              <article key={op.id} className="space-y-2 rounded border border-border p-3">
                <h4 className="font-medium">{labels[op.kind]}</h4>
                <p className="text-sm text-muted-foreground">
                  {new Date(op.createdAt).toLocaleString()} ·{' '}
                  {op.actorName ?? 'Recorded shop member'} · {op.memberCount}{' '}
                  {op.memberCount === 1 ? 'change' : 'changes'}
                </p>
                {op.bulk ? (
                  <p className="break-words text-sm">
                    {op.bulk.direction === 'raise' ? 'Raise' : 'Lower'}{' '}
                    {goalPercent(op.bulk.magnitudeBps)} ·{' '}
                    {op.bulk.selection.kind === 'category'
                      ? `Whole category: ${op.bulk.selection.categoryKey || 'Uncategorized'}`
                      : `Preferred vendor: ${op.bulk.selection.vendorId}`}
                  </p>
                ) : null}
                {op.undoSourceOperationId ? (
                  <p className="break-words text-sm">
                    Undo of{' '}
                    {op.undoSourceScopeKind === 'bulk'
                      ? 'bulk price change'
                      : 'goal raise'}{' '}
                    operation {op.undoSourceOperationId}
                  </p>
                ) : null}
                {!receiptSummary ? (
                  <OperationUndoAction
                    operation={op}
                    review={review}
                    enabled={
                      !blocked && !q.isFetching && !q.isPlaceholderData && !q.isError
                    }
                  />
                ) : null}
                <ul className="space-y-2">
                  {op.members.map((m) => (
                    <PriceChangeMember
                      key={m.id}
                      member={m}
                      undoSourceKind={op.undoSourceScopeKind}
                      review={review}
                      enabled={
                        !blocked && !q.isFetching && !q.isPlaceholderData && !q.isError
                      }
                      onInspect={inspect}
                    />
                  ))}
                </ul>
                {op.memberCount > op.members.length ? (
                  <Button
                    className="min-h-11"
                    disabled={review?.locked}
                    onClick={() => setDetail(op)}
                  >
                    View all {op.memberCount} changes
                  </Button>
                ) : null}
              </article>
            ))
          )}
          <Pager
            offset={linkedId ? 0 : offset}
            total={q.data.total}
            disabled={review?.locked}
            onPage={(n) => {
              if (!review?.locked) setOffset(n);
            }}
          />
        </>
      ) : (
        <UnresolvedState
          state={unresolved(shownState(q, q.data))}
          icon={IconInventory}
          what="price history"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      )}
      {detail ? (
        <MemberDetails
          operation={detail}
          receiptSummary={receiptSummary}
          itemId={itemId}
          review={review}
          blocked={blocked}
          onInspect={inspect}
          onClose={() => {
            if (!review?.locked) setDetail(null);
          }}
        />
      ) : null}
    </section>
  );
}
export function PriceChangesSheet({
  onClose,
  initialKind,
  operationId,
  review,
  blocked,
}: {
  onClose: () => void;
  initialKind?: Kind;
  operationId?: string;
  review?: PricingReviewWrite;
  blocked?: boolean;
}) {
  return (
    <Sheet open title="All price changes" onClose={onClose}>
      <PricingHistory
        key={operationId ?? 'all'}
        operationId={operationId}
        initialKind={initialKind}
        review={review}
        blocked={blocked}
      />
    </Sheet>
  );
}

export function ReceiptPriceResult({
  locator,
}: {
  locator: ReceiptPriceLocator | undefined;
}) {
  const session = useSession();
  if (!locator || (session.role !== 'owner' && session.role !== 'office')) return null;
  if (locator.availability === 'unavailable')
    return (
      <p className="text-sm text-muted-foreground">
        Price-raise summary unavailable for this older receipt.
      </p>
    );
  if (!locator.operationId)
    return (
      <p role="status" className="text-sm">
        No sell prices were raised by this receipt.
      </p>
    );
  return (
    <PricingHistory
      key={locator.operationId}
      operationId={locator.operationId}
      receiptSummary
    />
  );
}
